/**
 * GiftCardDetailModal.tsx
 *
 * Displays full details of a gift card and its transaction history.
 * Supports: editing title/notes/serial, adding balance, redeeming, voiding,
 * and adjusting the balance with a reason.
 * Matches legacy `payment_details_giftcard.php` functionality.
 *
 * Cards sold online (API v4) also show the buyer, recipient, delivery and
 * claim (online gift cards plan U13). While unclaimed, staff can resend the
 * delivery email, correct the recipient email (after checking the buyer's
 * contact details), or void the card and refund the buyer in full; the code is
 * masked, with Reveal for users the API lets see it (each reveal is logged).
 * Once claimed the card belongs to the recipient and only an adjustment with
 * a reason remains (AE7).
 */
import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getGiftCard,
  getTransactions,
  redeemGiftCard,
  reloadGiftCard,
  voidGiftCard,
  updateGiftCard,
  adjustGiftCard,
  changeGiftCardRecipient,
  resendGiftCardEmail,
  revealGiftCardCode,
  voidRefundGiftCard,
  type GiftCard,
  type GiftCardAction,
  type GiftCardTransaction,
  type RedeemGiftCardPayload,
  type ReloadGiftCardPayload,
  type UpdateGiftCardPayload,
} from '@/services/gift-cards.service';
import { listUsers, type AdminUser } from '@/services/admin.service';
import { getPaymentDetail } from '@/services/payments.service';
import { getErrorCode, getErrorMessage } from '@/services/apiClient';
import { PaymentDetailModal } from '@/pages/payments/PaymentDetailModal';
import { PatientLink } from '@/components/ui/PatientLink';
import { utcToWallClock } from '@/lib/facilityTimezone';
import { MAX_GIFT_CARD_AMOUNT, giftCardReloadHeadroom } from '@/lib/giftCards';
import { useUserTimezone } from '@/hooks/useUserTimezone';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface GiftCardDetailModalProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  giftCardId: number;
  /**
   * The payment this modal was opened from, when it was opened from one. Its
   * history link then returns to that payment (closes this modal) instead of
   * opening a second copy of it on top.
   */
  fromPaymentId?: number | null;
  readOnly?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTimeInTz(dateStr: string, tz: string): string {
  const wall = utcToWallClock(dateStr, tz);
  if (!wall) {
    const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return dateStr;
    const [, year, month, day, hourStr, minute] = match;
    let hours = parseInt(hourStr, 10);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${month}-${day}-${year} ${hours}:${minute}${ampm}`;
  }
  const [y, mo, d] = wall.date.split('-');
  const [hStr, mi] = wall.time.split(':');
  let hours = parseInt(hStr, 10);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${mo}-${d}-${y} ${hours}:${mi}${ampm}`;
}

function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

/** History labels for the API's action (KTD13). */
const ACTION_LABELS: Record<GiftCardAction, string> = {
  sale: 'Sale',
  issue: 'Issued',
  delivery: 'Delivery Email',
  resend: 'Email Resent',
  recipient_change: 'Recipient Changed',
  claim: 'Claimed',
  redeem: 'Redemption',
  restore: 'Restored',
  refund: 'Refund',
  void: 'Voided',
  void_refund: 'Voided & Refunded',
  reload: 'Balance Increase',
  adjust: 'Adjustment',
  edit: 'Edited',
  view_code: 'Code Viewed',
};

/** Label for a row's action, or null for rows written before actions existed. */
function actionLabel(action: string | undefined): string | null {
  const key = (action ?? '').trim();
  if (!key) return null;
  return ACTION_LABELS[key as GiftCardAction]
    ?? key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * The signed change a row made to the balance, for the Amount column; null
 * when it moved no money (a resend, a claim, a code view). Spending rows log
 * a positive amount, a restore logs a negative one (KTD10) and an adjustment
 * logs its signed amount.
 */
function actionDelta(action: string, amount: number): number | null {
  switch (action) {
    case 'redeem':
    case 'void':
    case 'void_refund':
      return -Math.abs(amount);
    case 'restore':
    case 'refund':
      return Math.abs(amount);
    case 'adjust':
      return amount;
    default:
      return amount === 0 ? null : amount;
  }
}

/** All but the last four characters hidden, the same shape the API masks with. */
function maskCode(code: string): string {
  if (code.includes('*')) return code;
  if (code.length <= 4) return '*'.repeat(code.length);
  return '*'.repeat(code.length - 4) + code.slice(-4);
}

/** Sold online and not yet claimed by anyone: delivery can still change. */
function isUnclaimedOnline(card: GiftCard): boolean {
  return !!card.isOnline && !card.claimedPatientId && !card.issuedToPatientId;
}

function deriveTransactionType(tx: GiftCardTransaction, prevBalance: number | null): string {
  const raw = tx as unknown as Record<string, unknown>;
  const sourcePaymentID = Number(raw.sourcePaymentID ?? raw.source_payment_id ?? 0);
  const targetPaymentID = Number(raw.targetPaymentID ?? raw.target_payment_id ?? 0);
  const currentBalance = Number(raw.balanceAmount ?? tx.balanceAfter ?? 0);

  if (tx.transactionType === 'void') return 'Voided';

  // Compare balances: if balance went down, it's a redemption
  if (prevBalance !== null && currentBalance < prevBalance) {
    return 'Redemption';
  }
  if (prevBalance !== null && currentBalance > prevBalance) {
    return 'Balance Increase';
  }

  // Fallback to payment ID heuristics
  if (targetPaymentID !== 0) return 'Redemption';
  if (sourcePaymentID > 0) return 'Balance Increase';

  if (tx.transactionType === 'redeem') return 'Redemption';
  if (tx.transactionType === 'reload') return 'Balance Increase';
  if (tx.transactionType === 'issue') return 'Balance Increase';
  return 'Balance Increase';
}

function getPaymentReference(tx: GiftCardTransaction): number | null {
  const raw = tx as unknown as Record<string, unknown>;
  const sourcePaymentID = Number(raw.sourcePaymentID ?? raw.source_payment_id ?? 0);
  const targetPaymentID = Number(raw.targetPaymentID ?? raw.target_payment_id ?? 0);
  if (targetPaymentID > 0) return targetPaymentID;
  if (sourcePaymentID > 0) return sourcePaymentID;
  if (tx.paymentId && tx.paymentId > 0) return tx.paymentId;
  return null;
}

function getUserId(tx: GiftCardTransaction): number | null {
  const raw = tx as unknown as Record<string, unknown>;
  return Number(raw.userID ?? raw.user_id ?? 0) || null;
}

/**
 * Open gift card modals, newest last. They can stack (a card opens a payment,
 * whose line opens another card), and only the top one answers Escape.
 */
const openGiftCardModals: symbol[] = [];

// ─── Component ────────────────────────────────────────────────────────────────

export default function GiftCardDetailModal({
  open,
  onClose,
  onChanged,
  giftCardId,
  fromPaymentId = null,
  readOnly = false,
}: GiftCardDetailModalProps) {
  const queryClient = useQueryClient();
  const userTz = useUserTimezone();

  // A history row's payment opens here, over this modal, as that payment's own
  // patient: a card's sale belongs to its buyer (or the guest placeholder) and
  // a redemption to whoever paid, not to the patient whose page this is on.
  const [paymentView, setPaymentView] = useState<{ paymentId: number; patientId: number; patientName?: string } | null>(null);
  const [openingPaymentId, setOpeningPaymentId] = useState<number | null>(null);
  const [paymentOpenError, setPaymentOpenError] = useState<string | null>(null);

  const openPayment = useCallback(
    async (paymentId: number) => {
      if (fromPaymentId && paymentId === fromPaymentId) {
        onClose();
        return;
      }
      setPaymentOpenError(null);
      setOpeningPaymentId(paymentId);
      try {
        // Same query the payment modal runs, so it opens from the cache.
        const detail = await queryClient.fetchQuery({
          queryKey: ['payment-detail', paymentId],
          queryFn: () => getPaymentDetail(paymentId),
        });
        const name = [detail.patientFirstName, detail.patientLastName].filter(Boolean).join(' ');
        setPaymentView({ paymentId, patientId: Number(detail.patientID ?? 0), patientName: name || undefined });
      } catch (err) {
        setPaymentOpenError(getErrorMessage(err, `Could not open payment #${paymentId}.`));
      } finally {
        setOpeningPaymentId(null);
      }
    },
    [fromPaymentId, onClose, queryClient],
  );

  // Escape closes what is on top: the payment opened from here, else this
  // modal. Caught in the capture phase and stopped there, because the shared
  // Modal listens on document too, and a payment modal this one was opened
  // from (underneath) would otherwise close instead.
  // Registered once per open (the latest state is read through refs), so a
  // parent re-render never moves this modal back to the top of the stack.
  const paymentViewRef = React.useRef(paymentView);
  paymentViewRef.current = paymentView;
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const self = Symbol('gift-card-modal');
    openGiftCardModals.push(self);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openGiftCardModals[openGiftCardModals.length - 1] !== self) return;
      e.stopImmediatePropagation();
      if (paymentViewRef.current) setPaymentView(null);
      else onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      const at = openGiftCardModals.indexOf(self);
      if (at >= 0) openGiftCardModals.splice(at, 1);
    };
  }, [open]);

  const {
    data: card,
    isLoading: cardLoading,
    isError: cardError,
  } = useQuery({
    queryKey: ['gift-card', giftCardId],
    queryFn: () => getGiftCard(giftCardId),
    enabled: open,
  });

  const {
    data: transactions,
    isLoading: txLoading,
    isError: txError,
  } = useQuery({
    queryKey: ['gift-card-transactions', giftCardId],
    queryFn: () => getTransactions(giftCardId),
    enabled: open,
  });

  const { data: adminUsers = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60 * 1000,
  });

  const usersById = React.useMemo(() => {
    const map = new Map<number, string>();
    adminUsers.forEach((u) => map.set(u.userId, `${u.firstName} ${u.lastName}`.trim()));
    return map;
  }, [adminUsers]);

  // ── Editable fields state ──────────────────────────────────────────────
  const [editTitle, setEditTitle] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!card) return;
    const decodeHtml = (str: string) => {
      const el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    };
    setEditTitle(decodeHtml(card.title ?? ''));
    setEditNotes(decodeHtml(card.notes ?? ''));
    setDirty(false);
  }, [card]);

  const handleFieldChange = (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(e.target.value);
      setDirty(true);
    };

  // ── Copy serial state ────────────────────────────────────────────────
  const [copiedSerial, setCopiedSerial] = useState(false);

  // ── Save metadata mutation ─────────────────────────────────────────────
  const [saveError, setSaveError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: (payload: UpdateGiftCardPayload) => updateGiftCard(giftCardId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gift-card', giftCardId] });
      onChanged();
      setDirty(false);
      setSaveError(null);
    },
    onError: (err) => {
      setSaveError(getErrorMessage(err, 'Failed to save changes.'));
    },
  });

  function handleSave() {
    saveMutation.mutate({
      title: editTitle.trim() || null,
      notes: editNotes.trim() || null,
    });
  }

  // ── Void mutation ──────────────────────────────────────────────────────
  const voidMutation = useMutation({
    mutationFn: () => voidGiftCard(giftCardId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gift-card', giftCardId] });
      void queryClient.invalidateQueries({ queryKey: ['gift-card-transactions', giftCardId] });
      onChanged();
    },
  });

  function handleVoid() {
    if (!window.confirm('Deleting this gift card will remove any remaining balance. Are you sure you want to delete this gift card?')) return;
    voidMutation.mutate();
  }

  // ── Redeem form state ──────────────────────────────────────────────────
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemError, setRedeemError] = useState<string | null>(null);

  const redeemMutation = useMutation({
    mutationFn: (payload: RedeemGiftCardPayload) => redeemGiftCard(giftCardId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gift-card', giftCardId] });
      void queryClient.invalidateQueries({ queryKey: ['gift-card-transactions', giftCardId] });
      onChanged();
      setRedeemAmount('');
      setRedeemError(null);
    },
    onError: (err) => {
      setRedeemError(getErrorMessage(err, 'Failed to process redemption.'));
    },
  });

  // ── Reload form state ─────────────────────────────────────────────────
  const [reloadAmount, setReloadAmount] = useState('');
  const [reloadError, setReloadError] = useState<string | null>(null);

  const reloadMutation = useMutation({
    mutationFn: (payload: ReloadGiftCardPayload) => reloadGiftCard(giftCardId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gift-card', giftCardId] });
      void queryClient.invalidateQueries({ queryKey: ['gift-card-transactions', giftCardId] });
      onChanged();
      setReloadAmount('');
      setReloadError(null);
    },
    onError: (err) => {
      setReloadError(getErrorMessage(err, 'Failed to add balance.'));
    },
  });

  // A reload tops the card up, so the cap applies to the resulting balance:
  // what can still be added is MAX_GIFT_CARD_AMOUNT minus what is on the card.
  const reloadHeadroom = giftCardReloadHeadroom(card?.currentBalance ?? 0);

  const handleReload = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setReloadError(null);
      const amount = parseFloat(reloadAmount);
      if (isNaN(amount) || amount <= 0) {
        setReloadError('Amount must be greater than 0.');
        return;
      }
      if (amount > reloadHeadroom) {
        setReloadError(
          `A gift card cannot exceed ${formatCurrency(MAX_GIFT_CARD_AMOUNT)}. ` +
          `At most ${formatCurrency(reloadHeadroom)} can be added to this card.`,
        );
        return;
      }
      reloadMutation.mutate({ amount });
    },
    [reloadAmount, reloadHeadroom, reloadMutation],
  );

  const handleRedeem = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setRedeemError(null);
      const amount = parseFloat(redeemAmount);
      if (isNaN(amount) || amount <= 0) {
        setRedeemError('Amount must be greater than 0.');
        return;
      }
      if (card && amount > card.currentBalance) {
        setRedeemError(`Amount cannot exceed the current balance of ${formatCurrency(card.currentBalance)}.`);
        return;
      }
      // Spent by its owner when it has one: a claimed online card needs the
      // patient paying for its owner rule, and a desk card with no owner
      // redeems without one, as it always has.
      const ownerId = card?.issuedToPatientId ?? card?.claimedPatientId ?? null;
      redeemMutation.mutate(ownerId ? { amount, patientId: ownerId } : { amount });
    },
    [redeemAmount, card, redeemMutation],
  );

  // Every staff tool below writes a card history row, so each refreshes the
  // history along with the card.
  const refreshCard = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['gift-card', giftCardId] });
    void queryClient.invalidateQueries({ queryKey: ['gift-card-transactions', giftCardId] });
    onChanged();
  }, [queryClient, giftCardId, onChanged]);

  // ── Adjust balance (any card; a reason is required, R25) ─────────────────
  const [adjustDirection, setAdjustDirection] = useState<'add' | 'remove'>('add');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const adjustMutation = useMutation({
    mutationFn: (payload: { amount: number; reason: string }) => adjustGiftCard(giftCardId, payload),
    onSuccess: () => {
      refreshCard();
      setAdjustAmount('');
      setAdjustReason('');
      setAdjustError(null);
    },
    onError: (err) => {
      setAdjustError(getErrorMessage(err, 'Failed to adjust the balance.'));
    },
  });

  const parsedAdjustAmount = parseFloat(adjustAmount);
  const adjustReady = !Number.isNaN(parsedAdjustAmount) && parsedAdjustAmount > 0 && adjustReason.trim() !== '';

  const handleAdjust = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setAdjustError(null);
      const reason = adjustReason.trim();
      if (!reason) {
        setAdjustError('Enter a reason for the adjustment.');
        return;
      }
      const amount = parseFloat(adjustAmount);
      if (isNaN(amount) || amount <= 0) {
        setAdjustError('Amount must be greater than 0.');
        return;
      }
      const balance = card?.currentBalance ?? 0;
      if (adjustDirection === 'remove' && amount > balance) {
        setAdjustError(`At most ${formatCurrency(balance)} can be removed from this card.`);
        return;
      }
      if (adjustDirection === 'add' && amount > giftCardReloadHeadroom(balance)) {
        setAdjustError(
          `A gift card cannot exceed ${formatCurrency(MAX_GIFT_CARD_AMOUNT)}. ` +
          `At most ${formatCurrency(giftCardReloadHeadroom(balance))} can be added to this card.`,
        );
        return;
      }
      adjustMutation.mutate({
        amount: Math.round((adjustDirection === 'remove' ? -amount : amount) * 100) / 100,
        reason,
      });
    },
    [adjustAmount, adjustReason, adjustDirection, card, adjustMutation],
  );

  // ── Masked code (unclaimed online cards, KTD19) ──────────────────────────
  // The API masks the code for everyone (codeMasked). The full code only
  // comes from the reveal endpoint, which needs the Gift Credit permission
  // and logs each view, so Reveal is offered to users who can manage gift
  // cards (not readOnly). A 403 there means the permission was lost: hide it.
  const [revealedCode, setRevealedCode] = useState<string | null>(null);
  const [revealForbidden, setRevealForbidden] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  const revealMutation = useMutation({
    mutationFn: () => revealGiftCardCode(giftCardId),
    onSuccess: (code) => {
      setRevealedCode(code);
      setRevealError(null);
      void queryClient.invalidateQueries({ queryKey: ['gift-card-transactions', giftCardId] });
    },
    onError: (err) => {
      if (getErrorCode(err) === 'FORBIDDEN') {
        setRevealForbidden(true);
        setRevealError('You do not have permission to see this code.');
        return;
      }
      setRevealError(getErrorMessage(err, 'Failed to reveal the code.'));
    },
  });

  // ── Delivery: resend / change recipient email (unclaimed online, R23) ────
  const [deliveryNotice, setDeliveryNotice] = useState<string | null>(null);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');

  const resendMutation = useMutation({
    mutationFn: () => resendGiftCardEmail(giftCardId),
    onSuccess: (result) => {
      refreshCard();
      setDeliveryError(null);
      setDeliveryNotice(`Delivery email sent to ${result.toEmail}.`);
    },
    onError: (err) => {
      // A failed send is still logged in the history.
      refreshCard();
      setDeliveryNotice(null);
      setDeliveryError(getErrorMessage(err, 'Failed to resend the delivery email.'));
    },
  });

  const changeRecipientMutation = useMutation({
    mutationFn: (email: string) => changeGiftCardRecipient(giftCardId, email),
    onSuccess: (result) => {
      refreshCard();
      // The change issued a new code, so a code revealed before it no longer
      // works: mask it again until staff reveal the new one.
      setRevealedCode(null);
      setChangeEmailOpen(false);
      setNewRecipientEmail('');
      if (result.delivery.sent) {
        setDeliveryError(null);
        setDeliveryNotice(
          `Recipient changed to ${result.recipientEmail}. A new code was issued and emailed there; ` +
          'the old code no longer works. The buyer and the previous address were notified.',
        );
      } else {
        // delivery.error says what to do; the mailer's own reason stays in
        // delivery.details and the card's history.
        setDeliveryNotice(null);
        setDeliveryError(
          `Recipient changed to ${result.recipientEmail} and a new code was issued, but the email wasn't sent. ` +
          (result.delivery.error ?? 'Use Resend Email to try again.'),
        );
      }
    },
    onError: (err) => {
      setDeliveryNotice(null);
      setDeliveryError(getErrorMessage(err, 'Failed to change the recipient email.'));
    },
  });

  function handleChangeRecipient(e: React.FormEvent) {
    e.preventDefault();
    const email = newRecipientEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setDeliveryNotice(null);
      setDeliveryError('Enter a valid email address.');
      return;
    }
    setDeliveryError(null);
    changeRecipientMutation.mutate(email);
  }

  // ── Void & refund (unclaimed online only, R24 / AE7) ─────────────────────
  const [voidRefundOpen, setVoidRefundOpen] = useState(false);
  const [voidRefundReason, setVoidRefundReason] = useState('');
  const [voidRefundError, setVoidRefundError] = useState<string | null>(null);
  const [voidRefundNotice, setVoidRefundNotice] = useState<string | null>(null);

  const voidRefundMutation = useMutation({
    mutationFn: (reason: string) => voidRefundGiftCard(giftCardId, reason),
    onSuccess: (result) => {
      refreshCard();
      setVoidRefundOpen(false);
      setVoidRefundReason('');
      setVoidRefundError(null);
      setVoidRefundNotice(
        `Card voided. ${formatCurrency(Number(result.refundAmount ?? 0))} refunded to the buyer ` +
        `on payment #${result.salePaymentId}.`,
      );
    },
    onError: (err) => {
      // A refund Forward accepted but couldn't record still voids the card
      // (GIFT_CARD_REFUND_NOT_RECORDED), and a refused attempt can leave a
      // 'failed' history row, so the card is re-read either way.
      refreshCard();
      setVoidRefundError(getErrorMessage(err, 'Failed to void and refund the gift card.'));
    },
  });

  if (!open) return null;

  const unclaimedOnline = card ? isUnclaimedOnline(card) : false;
  // An unclaimed online card has no patient yet: spending it belongs at the
  // desk (where it attaches to the patient paying), not in a manual redeem.
  const canRedeem = !readOnly && card?.active && (card?.currentBalance ?? 0) > 0 && !unclaimedOnline;
  const canReload = !readOnly && card?.active;
  const canAdjust = !readOnly;
  const canManageDelivery = !readOnly && unclaimedOnline && !!card?.active;
  const codeHidden = unclaimedOnline && revealedCode === null;
  const shownCode = card ? (revealedCode ?? (codeHidden ? maskCode(card.serialNumber) : card.serialNumber)) : '';
  const canReveal = codeHidden && !readOnly && !revealForbidden;

  // Mounted on document.body, like the shared Modal, so it stacks above a
  // payment modal it was opened from instead of inside it.
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">Gift Card Details</h2>
          <div className="flex items-center gap-2">
            {/* Desk cards only. An online card is voided with a refund to the
                buyer while unclaimed, and only adjusted once claimed. */}
            {!readOnly && card?.active && !card.isOnline && (card.redeemedAmount ?? 0) === 0 && (
              <button
                onClick={handleVoid}
                disabled={voidMutation.isPending}
                className="text-xs font-medium text-red-600 hover:text-red-800 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {voidMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-2xl leading-none font-light"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {cardLoading ? (
            <p className="text-sm text-slate-400 text-center py-8">Loading gift card…</p>
          ) : cardError || !card ? (
            <p className="text-sm text-red-500 text-center py-8">Failed to load gift card.</p>
          ) : (
            <>
              {/* ── Balance Summary ── */}
              <div className="flex items-center gap-4">
                <div className="flex-1 rounded-lg bg-blue-50 px-4 py-3 text-center">
                  <p className="text-xs text-blue-600 uppercase tracking-wide font-medium">Total</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(card.totalValue ?? (card.redeemedAmount + card.currentBalance))}</p>
                </div>
                <div className="flex-1 rounded-lg bg-slate-50 px-4 py-3 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Redeemed</p>
                  <p className="text-lg font-bold text-slate-700">{formatCurrency(card.redeemedAmount ?? 0)}</p>
                </div>
                <div className="flex-1 rounded-lg bg-emerald-50 px-4 py-3 text-center">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide font-medium">Remaining</p>
                  <p className="text-lg font-bold text-emerald-700">{formatCurrency(card.currentBalance)}</p>
                </div>
                {!card.active && card.currentBalance > 0 && (
                  <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">Voided</span>
                )}
              </div>

              {/* ── Serial + Status ── */}
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-slate-900">{shownCode}</span>
                {canReveal && (
                  <button
                    type="button"
                    onClick={() => revealMutation.mutate()}
                    disabled={revealMutation.isPending}
                    className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-medium px-2 py-0.5 rounded transition-colors disabled:opacity-50"
                  >
                    {revealMutation.isPending ? 'Revealing…' : 'Reveal'}
                  </button>
                )}
                {!codeHidden && (
                  <button
                    onClick={() => {
                      void navigator.clipboard.writeText(shownCode).then(() => {
                        setCopiedSerial(true);
                        setTimeout(() => setCopiedSerial(false), 2000);
                      });
                    }}
                    className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-medium px-2 py-0.5 rounded transition-colors"
                  >
                    {copiedSerial ? 'Copied!' : 'Copy'}
                  </button>
                )}
                {(() => {
                  const fullyRedeemed = card.currentBalance === 0 && (card.redeemedAmount ?? 0) > 0;
                  if (fullyRedeemed) {
                    return <span className="ml-auto rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">Redeemed</span>;
                  }
                  if (!card.active) {
                    return <span className="ml-auto rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">Voided</span>;
                  }
                  if (card.expiresDate && new Date(card.expiresDate) < new Date()) {
                    return <span className="ml-auto rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Expired</span>;
                  }
                  return <span className="ml-auto rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Active</span>;
                })()}
              </div>

              {revealError && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{revealError}</p>
              )}

              {/* ── Online purchase: buyer, recipient, delivery, claim ── */}
              {card.isOnline && (
                <div className="space-y-3 rounded-lg border border-slate-200 p-4 text-sm">
                  <h3 className="text-sm font-semibold text-slate-900">Online Purchase</h3>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-medium text-slate-500">Buyer</dt>
                      <dd className="text-slate-800">
                        {card.buyerPatientId ? (
                          <PatientLink
                            patientId={card.buyerPatientId}
                            patientName={card.buyerName ?? `Patient #${card.buyerPatientId}`}
                            className="text-blue-600 hover:underline"
                          >
                            {card.buyerName ?? `Patient #${card.buyerPatientId}`}
                          </PatientLink>
                        ) : (
                          card.buyerName ?? '—'
                        )}
                        {card.buyerEmail && <div className="text-xs text-slate-500">{card.buyerEmail}</div>}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-slate-500">Recipient</dt>
                      <dd className="text-slate-800">
                        {card.sendToSelf ? 'The buyer (sent to themselves)' : (card.recipientName ?? '—')}
                        {!card.sendToSelf && card.recipientEmail && (
                          <div className="text-xs text-slate-500">{card.recipientEmail}</div>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-slate-500">Delivery Email</dt>
                      <dd className="text-slate-800">
                        {card.deliveryStatus
                          ? card.deliveryStatus.charAt(0).toUpperCase() + card.deliveryStatus.slice(1)
                          : '—'}
                        {card.deliverySentDate && (
                          <span className="ml-1 text-xs text-slate-500">
                            {formatDateTimeInTz(card.deliverySentDate, userTz)}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-slate-500">Claim</dt>
                      <dd className="text-slate-800">
                        {card.claimedPatientId ? (
                          <>
                            Claimed by{' '}
                            <PatientLink
                              patientId={card.claimedPatientId}
                              patientName={card.claimedPatientName ?? `Patient #${card.claimedPatientId}`}
                              className="font-medium text-blue-600 hover:underline"
                            >
                              {card.claimedPatientName ?? `Patient #${card.claimedPatientId}`}
                            </PatientLink>
                            {card.claimedDate && (
                              <span className="ml-1 text-xs text-slate-500">
                                {formatDateTimeInTz(card.claimedDate, userTz)}
                              </span>
                            )}
                          </>
                        ) : card.issuedToPatientId ? (
                          <>
                            On the profile of{' '}
                            <PatientLink
                              patientId={card.issuedToPatientId}
                              patientName={card.issuedToPatientName ?? `Patient #${card.issuedToPatientId}`}
                              className="font-medium text-blue-600 hover:underline"
                            >
                              {card.issuedToPatientName ?? `Patient #${card.issuedToPatientId}`}
                            </PatientLink>
                          </>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                            Not claimed
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                  {card.message && (
                    <p className="rounded-md bg-slate-50 px-3 py-2 text-slate-700 whitespace-pre-line">{card.message}</p>
                  )}

                  {canManageDelivery && (
                    <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                      <button
                        type="button"
                        onClick={() => { setDeliveryNotice(null); setDeliveryError(null); resendMutation.mutate(); }}
                        disabled={resendMutation.isPending}
                        className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
                      >
                        {resendMutation.isPending ? 'Sending…' : 'Resend Email'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setChangeEmailOpen((v) => !v); setDeliveryNotice(null); setDeliveryError(null); }}
                        className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-md transition-colors"
                      >
                        Change Email
                      </button>
                      <button
                        type="button"
                        onClick={() => { setVoidRefundOpen(true); setVoidRefundError(null); }}
                        className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 transition-colors"
                      >
                        Void &amp; Refund
                      </button>
                    </div>
                  )}

                  {canManageDelivery && changeEmailOpen && (
                    <form onSubmit={handleChangeRecipient} className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                      {/* Verify the request against the buyer before the code goes
                          to a new address (KTD19). */}
                      <div data-testid="buyer-contact" className="text-xs text-slate-600">
                        <p className="font-medium text-slate-700">Verify with the buyer before saving:</p>
                        <p>{card.buyerName || '—'}</p>
                        <p>{card.buyerEmail || 'No email on file'}</p>
                        <p>{card.buyerPhone || 'No phone on file'}</p>
                      </div>
                      <div>
                        <label htmlFor="gc-new-recipient-email" className="block text-xs font-medium text-slate-600 mb-1">
                          New recipient email
                        </label>
                        <input
                          id="gc-new-recipient-email"
                          type="email"
                          value={newRecipientEmail}
                          onChange={(e) => setNewRecipientEmail(e.target.value)}
                          placeholder={card.recipientEmail ?? 'name@example.com'}
                          className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-slate-500">
                          The delivery email is sent to the new address. The buyer and the previous address get a notice.
                        </p>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => { setChangeEmailOpen(false); setNewRecipientEmail(''); }}
                          className="border border-slate-300 hover:bg-white text-xs font-medium px-3 py-1 rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={changeRecipientMutation.isPending || !newRecipientEmail.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1 rounded-md transition-colors disabled:opacity-50"
                        >
                          {changeRecipientMutation.isPending ? 'Saving…' : 'Save & Resend'}
                        </button>
                      </div>
                    </form>
                  )}

                  {canManageDelivery && voidRefundOpen && (
                    <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-3">
                      <p className="text-sm text-red-800">
                        Void this card and refund the buyer in full through the original payment.
                        The card can&apos;t be used afterwards. This cannot be undone.
                      </p>
                      <div>
                        <label htmlFor="gc-void-refund-reason" className="block text-xs font-medium text-slate-600 mb-1">
                          Reason (optional)
                        </label>
                        <input
                          id="gc-void-refund-reason"
                          type="text"
                          value={voidRefundReason}
                          onChange={(e) => setVoidRefundReason(e.target.value)}
                          maxLength={500}
                          className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => { setVoidRefundOpen(false); setVoidRefundReason(''); }}
                          disabled={voidRefundMutation.isPending}
                          className="border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium px-3 py-1 rounded-md transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => voidRefundMutation.mutate(voidRefundReason.trim())}
                          disabled={voidRefundMutation.isPending}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1 rounded-md transition-colors disabled:opacity-50"
                        >
                          {voidRefundMutation.isPending ? 'Refunding…' : 'Confirm Void & Refund'}
                        </button>
                      </div>
                    </div>
                  )}

                  {deliveryNotice && (
                    <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">{deliveryNotice}</p>
                  )}
                  {deliveryError && (
                    <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{deliveryError}</p>
                  )}
                  {voidRefundNotice && (
                    <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">{voidRefundNotice}</p>
                  )}
                  {/* Outside the active-card tools: a refund Forward accepted but
                      couldn't record voids the card, which hides those tools, and
                      staff still need this message. */}
                  {voidRefundError && (
                    <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{voidRefundError}</p>
                  )}
                </div>
              )}

              {/* ── Editable Fields ── */}
              <div className="space-y-3 rounded-lg border border-slate-200 p-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={handleFieldChange(setEditTitle)}
                    disabled={readOnly || !card.active}
                    className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                  <textarea
                    value={editNotes}
                    onChange={handleFieldChange(setEditNotes)}
                    rows={3}
                    disabled={readOnly || !card.active}
                    className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm disabled:bg-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
                {saveError && (
                  <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{saveError}</p>
                )}
                {!readOnly && dirty && card.active && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={saveMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded-md transition-colors disabled:opacity-50"
                    >
                      {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              {/* ── Add Balance ── */}
              {canReload && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <form onSubmit={handleReload} className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900 whitespace-nowrap">Add Balance</h3>
                    <div className="flex items-center gap-0 flex-1">
                      <span className="text-sm text-slate-500">$</span>
                      <input
                        type="number"
                        min="0.01"
                        max={reloadHeadroom}
                        step="0.01"
                        value={reloadAmount}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const n = parseFloat(raw);
                          setReloadAmount(
                            !Number.isNaN(n) && n > reloadHeadroom ? String(reloadHeadroom) : raw,
                          );
                        }}
                        disabled={reloadHeadroom <= 0}
                        placeholder="0.00"
                        className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-slate-100 disabled:text-slate-400"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={reloadMutation.isPending || reloadHeadroom <= 0}
                      className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-3 py-1 rounded-md transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {reloadMutation.isPending ? 'Adding…' : 'Add'}
                    </button>
                  </form>
                  {reloadHeadroom <= 0 && (
                    <p className="mt-2 text-xs text-slate-500">
                      This card is at the {formatCurrency(MAX_GIFT_CARD_AMOUNT)} maximum — no further
                      balance can be added.
                    </p>
                  )}
                  {reloadError && (
                    <p className="mt-2 rounded-md bg-red-50 px-3 py-1.5 text-sm text-red-700">{reloadError}</p>
                  )}
                </div>
              )}

              {/* ── Redeem ── */}
              {canRedeem && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <form onSubmit={handleRedeem} className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-900 whitespace-nowrap">Redeem</h3>
                    <div className="flex items-center gap-0 flex-1">
                      <span className="text-sm text-slate-500">$</span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        max={card.currentBalance}
                        value={redeemAmount}
                        onChange={(e) => setRedeemAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={redeemMutation.isPending}
                      className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1 rounded-md transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {redeemMutation.isPending ? 'Redeeming…' : 'Redeem'}
                    </button>
                  </form>
                  <p className="mt-1.5 text-xs text-slate-500">Gift cards should be redeemed through payments.<br />This section is only for manual adjustments if necessary.</p>
                  {redeemError && (
                    <p className="mt-2 rounded-md bg-red-50 px-3 py-1.5 text-sm text-red-700">{redeemError}</p>
                  )}
                </div>
              )}

              {/* ── Adjust Balance ── a correction up or down, with a required
                  reason (R25). The only change left once an online card is
                  claimed (AE7). */}
              {canAdjust && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <form onSubmit={handleAdjust} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-900 whitespace-nowrap">Adjust Balance</h3>
                      <select
                        aria-label="Adjustment direction"
                        value={adjustDirection}
                        onChange={(e) => setAdjustDirection(e.target.value === 'remove' ? 'remove' : 'add')}
                        className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="add">Add</option>
                        <option value="remove">Remove</option>
                      </select>
                      <div className="flex items-center gap-0">
                        <span className="text-sm text-slate-500">$</span>
                        <input
                          aria-label="Adjustment amount"
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={adjustAmount}
                          onChange={(e) => setAdjustAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        aria-label="Adjustment reason"
                        type="text"
                        value={adjustReason}
                        onChange={(e) => setAdjustReason(e.target.value)}
                        maxLength={500}
                        placeholder="Reason (required)"
                        className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="submit"
                        disabled={adjustMutation.isPending || !adjustReady}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-1 rounded-md transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {adjustMutation.isPending ? 'Adjusting…' : 'Adjust'}
                      </button>
                    </div>
                  </form>
                  {adjustError && (
                    <p className="mt-2 rounded-md bg-red-50 px-3 py-1.5 text-sm text-red-700">{adjustError}</p>
                  )}
                </div>
              )}

              {/* ── Transaction History ── */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">History</h3>
                {paymentOpenError && (
                  <p className="mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{paymentOpenError}</p>
                )}
                {txLoading ? (
                  <p className="text-sm text-slate-400 text-center py-4">Loading transactions…</p>
                ) : txError ? (
                  <p className="text-sm text-red-500 text-center py-4">Failed to load transactions.</p>
                ) : !transactions || transactions.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No transactions yet.</p>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Payment</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transactions.map((tx: GiftCardTransaction, idx: number) => {
                          const raw = tx as unknown as Record<string, unknown>;
                          const txKey = tx.transactionId ?? raw.giftCardLogID ?? idx;
                          // Transactions are ordered desc; the next row is the previous state in time
                          const nextTx = idx < transactions.length - 1 ? transactions[idx + 1] : null;
                          const prevBalance = nextTx
                            ? Number((nextTx as unknown as Record<string, unknown>).balanceAmount ?? nextTx.balanceAfter ?? null)
                            : null;
                          // The API's action names the row (KTD13); rows written
                          // before actions existed keep the guessed label.
                          const action = (tx.action ?? '').trim();
                          const txType = actionLabel(action) ?? deriveTransactionType(tx, prevBalance);
                          const amount = Number(tx.amount ?? 0);
                          const delta = action
                            ? actionDelta(action, amount)
                            : (txType === 'Redemption' ? -Math.abs(amount) : amount);
                          const paymentRef = getPaymentReference(tx);
                          const txUserId = getUserId(tx);
                          const userName = txUserId ? (usersById.get(txUserId) ?? `User #${txUserId}`) : '—';
                          const txDate = tx.createdAt ?? (raw.createDate as string) ?? '';

                          return (
                            <tr key={String(txKey)} className="hover:bg-slate-50 transition-colors">
                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                                {txDate ? formatDateTimeInTz(txDate, userTz) : '—'}
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                <div>{txType}</div>
                                {tx.reason ? (
                                  <div className="text-xs text-slate-500 break-words">{tx.reason}</div>
                                ) : null}
                              </td>
                              <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${delta !== null && delta < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                                {delta === null ? '—' : `${delta < 0 ? '-' : ''}${formatCurrency(Math.abs(delta))}`}
                              </td>
                              <td className="px-3 py-2">
                                {paymentRef ? (
                                  <button
                                    type="button"
                                    onClick={() => void openPayment(paymentRef)}
                                    disabled={openingPaymentId === paymentRef}
                                    className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                                    title={
                                      fromPaymentId === paymentRef
                                        ? `Back to payment #${paymentRef}`
                                        : `Open payment #${paymentRef}`
                                    }
                                  >
                                    {openingPaymentId === paymentRef ? 'Opening…' : `Payment #${paymentRef}`}
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-400">Manual</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-slate-500 text-xs">
                                {userName}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-slate-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="border border-slate-300 hover:bg-slate-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {paymentView && (
        <PaymentDetailModal
          paymentId={paymentView.paymentId}
          patientId={paymentView.patientId}
          patientName={paymentView.patientName}
          open
          onClose={() => setPaymentView(null)}
        />
      )}
    </div>,
    document.body,
  );
}
